import { Helmet } from "react-helmet";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useEffect, useState } from "react";

import PlusIcon from "./../../../assets/icons/plus.svg?react";
import MoreIcon from "./../../../assets/icons/more.svg?react";
// import ArrowRightIcon from "./../../../assets/icons/arrow-right.svg?react";
import ArchiveIcon from "./../../../assets/icons/archive-box.svg?react";
import RefreshIcon from "./../../../assets/icons/arrow-path.svg?react";

import { useNavigate } from "react-router-dom";
import { DataServiceGeneralInformationUrl } from "../../../utils/urls";
import { DeleteModal } from "../../modals/QuestionModal";
import { useSearch } from "../../../providers/SearchProvider";

import {
  deleteDSSReplicationAsync,
  getDBSServiceIntegrationsAsync,
  getDSSReplicationsAsync,
  handleShowIPsEditorModal,
  handleToggleDSSReplicationModal,
  selectDatabaseService,
  selectDatabaseServiceLoading,
  selectDBSIntegrations,
  selectDBSIntegrationsLoading,
  selectDSSReplications,
  selectDSSReplicationsActionLoading,
  selectDSSReplicationsLoading,
} from "../../../store/database/serviceSlice";
import EditIcon from "./../../../assets/icons/pencil-square.svg?react";
import { generateServicePermissions } from "../../../pages/databases/ServicePage";
import {
  Button,
  Dropdown,
  EmptyState,
  Flex,
  SimpleTable,
  Skeleton,
  Tag,
  Typography,
} from "djuno-design";
import HighlighterText from "../../general/HighlighterText";
import { ReplicationFlow } from "../../../types/data-straming";
import { ReplicationFlowCreateModal } from "./ReplicationFlowCreateModal";
import {
  getDatabaseServicesAsync,
  selectDatabaseServices,
  selectDatabaseServicesLoading,
} from "../../../store/database/servicesSlice";

const ReplicationFlowsTab = () => {
  const [deleteReplication, setDeleteReplication] =
    useState<ReplicationFlow | null>(null);

  const replications = useAppSelector(selectDSSReplications);
  const replicationsLoading = useAppSelector(selectDSSReplicationsLoading);
  const [filteredReplications, setFilteredReplications] = useState<
    ReplicationFlow[]
  >([]);

  const service = useAppSelector(selectDatabaseService);
  const loading = useAppSelector(selectDatabaseServiceLoading);

  const actionLoading = useAppSelector(selectDSSReplicationsActionLoading);

  const integrations = useAppSelector(selectDBSIntegrations);
  const integrationsLoading = useAppSelector(selectDBSIntegrationsLoading);

  const services = useAppSelector(selectDatabaseServices);
  const servicesLoading = useAppSelector(selectDatabaseServicesLoading);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { value: searchValue } = useSearch();

  useEffect(() => {
    if (service) {
      const permissions = generateServicePermissions().replicationFlows;
      if (!permissions.includes(service.engine)) {
        navigate(DataServiceGeneralInformationUrl(service.engine, service.id));
      } else {
        dispatch(
          getDSSReplicationsAsync({
            engine: service.engine,
            clusterId: service.id,
          })
        );
      }
    }
  }, [dispatch, navigate, service]);

  useEffect(() => {
    // const lookedUpFiles = replications?.filter((r) =>
    //   r.name?.toLowerCase().includes(searchValue.toLowerCase())
    // );
    setFilteredReplications(replications);
  }, [dispatch, replications]);

  useEffect(() => {
    if (service && integrations.length === 0)
      dispatch(
        getDBSServiceIntegrationsAsync({
          clusterId: service.id,
          engine: service.engine,
        })
      );
  }, [dispatch, integrations.length, service]);

  useEffect(() => {
    if (service && services.length === 0) {
      dispatch(getDatabaseServicesAsync({}));
    }
  }, [dispatch, service, services.length]);

  return (
    <>
      {/* <Helmet>
        <title>{import.meta.env.VITE_APP_NAME}</title>
        <meta name="description" content="" />
      </Helmet> */}

      {service && (
        <>
          <div className="flex items-center justify-between">
            <div className="items-center justify-between flex flex-1 transition duration-150">
              <Typography.Title level={5} className="!mb-0">
                Replication Flows
              </Typography.Title>
            </div>
            <div className="flex items-center gap-2">
              <Button
                uiType="light"
                onClick={() => {
                  if (service) {
                    dispatch(
                      getDSSReplicationsAsync({
                        engine: service.engine,
                        clusterId: service.id,
                      })
                    );
                  }
                }}
                disabled={!service}
                className="group"
                tooltip={{ content: "Refresh" }}
              >
                <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
              </Button>
              <Button
                uiType="primary"
                disabled={
                  integrationsLoading ||
                  integrations.filter((i) => i.type === "kafkaMirrorMaker")
                    .length < 2
                }
                onClick={() =>
                  dispatch(
                    handleToggleDSSReplicationModal({
                      status: true,
                    })
                  )
                }
                className="group"
                loading={integrationsLoading}
              >
                Add a replication
                <PlusIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
              </Button>
            </div>
          </div>

          <div className="mt-5 w-full">
            <SimpleTable
              loading={loading || replicationsLoading}
              containerClassName="min-h-[240px]"
            >
              <SimpleTable.Head>
                <SimpleTable.Row>
                  <SimpleTable.TH lable="Source" />
                  <SimpleTable.TH lable="Target" />
                  <SimpleTable.TH lable="Topics" />
                  <SimpleTable.TH lable="Topics blacklists" />
                  <SimpleTable.TH lable="Sync group effects" />
                  <SimpleTable.TH lable="Sync intervals(s)" />
                  <SimpleTable.TH lable="Status" />
                  <SimpleTable.TH lable="" />
                </SimpleTable.Row>
              </SimpleTable.Head>
              <SimpleTable.Body>
                {filteredReplications.map((replication, i) => {
                  const sourceIntegration = integrations.find(
                    (i) => i.id === replication.sourceIntegration
                  );
                  const sourceService = services.find(
                    (s) => s.id === sourceIntegration?.sourceServiceId
                  );

                  const targetIntegration = integrations.find(
                    (i) => i.id === replication.targetIntegration
                  );
                  const targetService = services.find(
                    (s) => s.id === targetIntegration?.sourceServiceId
                  );
                  return (
                    <SimpleTable.Row key={i}>
                      <SimpleTable.TD>
                        <SimpleTable.TD>
                          {(servicesLoading || integrationsLoading) && (
                            <Skeleton style={{ width: 130, height: 30 }} />
                          )}
                          {!servicesLoading && !integrationsLoading && (
                            <Typography.Text className="!text-xs md:!text-sm whitespace-nowrap">
                              {sourceService?.description}
                            </Typography.Text>
                          )}
                        </SimpleTable.TD>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        {(servicesLoading || integrationsLoading) && (
                          <Skeleton style={{ width: 130, height: 30 }} />
                        )}
                        {!servicesLoading && !integrationsLoading && (
                          <Typography.Text className="!text-xs md:!text-sm whitespace-nowrap">
                            {targetService?.description}
                          </Typography.Text>
                        )}
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Flex className="!flex-wrap gap-1">
                          {replication.topics.map((t, i) => (
                            <Tag key={t + i}>{t}</Tag>
                          ))}
                        </Flex>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Flex className="!flex-wrap gap-1">
                          {replication.topicExcludeList.map((t, i) => (
                            <Tag key={t + i}>{t}</Tag>
                          ))}
                        </Flex>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {replication.syncGroupOffsets ? "Yes" : "No"}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {replication.syncInterval}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD>
                        <Typography.Text className="!text-xs md:!text-sm">
                          {replication.enabled ? "Enabled" : "Disabled"}
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD className="w-20 gap-1">
                        <div className="h-full w-full inline-flex items-center justify-end gap-1">
                          <div className="w-8 flex justify-center items-center">
                            <Dropdown
                              itemsClassName="!w-48"
                              anchor="bottom end"
                              menu={[
                                {
                                  key: "1",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <EditIcon className="w-4"></EditIcon>
                                      Modify
                                    </div>
                                  ),
                                  onClick: (_, close) => {
                                    close();
                                    dispatch(
                                      handleToggleDSSReplicationModal({
                                        status: true,
                                        replication,
                                      })
                                    );
                                  },
                                  disabled: loading,
                                },

                                {
                                  type: "divider",
                                },
                                {
                                  key: "end",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <ArchiveIcon className="w-4" />
                                      Delete
                                    </div>
                                  ),
                                  danger: true,
                                  onClick: (_, close) => {
                                    close();
                                    setDeleteReplication(replication);
                                  },
                                  disabled: loading,
                                },
                              ]}
                            >
                              <Button
                                uiType="icon"
                                uiSize="small"
                                className="!px-2"
                              >
                                <MoreIcon className="w-4 h-4" />
                              </Button>
                            </Dropdown>
                          </div>
                        </div>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  );
                })}

                {replications.length === 0 && (
                  <SimpleTable.Row withoutHoverStyle className="h-[200px]">
                    <SimpleTable.TD colSpan={7} className="!border-0">
                      <EmptyState text="You have not created any Replication Flow yet" />
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                )}
              </SimpleTable.Body>
            </SimpleTable>
          </div>
          <ReplicationFlowCreateModal />

          <DeleteModal
            title="Delete Replication Flow"
            isOpen={deleteReplication !== null}
            onClose={() => setDeleteReplication(null)}
            description=""
            confirmButtonText="Confirm"
            confirmButtonType="danger"
            confirmButtonClassName="w-full"
            onConfirm={async () => {
              if (!deleteReplication || !service) {
                setDeleteReplication(null);
                return;
              }

              const action = await dispatch(
                deleteDSSReplicationAsync({
                  engine: service.engine,
                  clusterId: service.id,
                  replicationId: deleteReplication.id,
                })
              );

              if (action.type === "service/replications/delete/fulfilled") {
                await dispatch(
                  getDSSReplicationsAsync({
                    engine: service.engine,
                    clusterId: service.id,
                  })
                );
              }

              setDeleteReplication(null);
            }}
            loading={actionLoading}
            // confirmString={deleteReplication?.id}
          />
        </>
      )}
    </>
  );
};

export default ReplicationFlowsTab;
